// =============================================================================
// Add/Edit Viewer Modal
// =============================================================================
// Full-featured modal with access level presets and advanced permission toggles
// =============================================================================

import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  EyeIcon,
  KeyIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { adminService } from '../../../services/admin.service';
import type {
  TeamMemberResponse,
  TeamAccessLevel,
  TeamPermissions,
  AccessLevelPreset,
  DealWithStats,
} from '../../../services/admin.service';

// =============================================================================
// Access Level Card Config
// =============================================================================

const levelIcons: Record<TeamAccessLevel, React.ComponentType<{ className?: string }>> = {
  FULL_ACCESS: ShieldCheckIcon,
  ASSET_MANAGER: BuildingOfficeIcon,
  INVESTOR_RELATIONS: UserGroupIcon,
  DEAL_ACCESS: KeyIcon,
  VIEW_ONLY: EyeIcon,
};

// =============================================================================
// Component
// =============================================================================

interface ViewerModalProps {
  member: TeamMemberResponse | null; // null = create mode
  onClose: (saved: boolean) => void;
}

export function ViewerModal({ member, onClose }: ViewerModalProps) {
  const isEditing = !!member;

  // Form state
  const [email, setEmail] = useState(member?.email || '');
  const [firstName, setFirstName] = useState(member?.firstName || '');
  const [lastName, setLastName] = useState(member?.lastName || '');
  const [selectedLevel, setSelectedLevel] = useState<TeamAccessLevel>(member?.accessLevel || 'VIEW_ONLY');
  const [permissions, setPermissions] = useState<TeamPermissions | null>(member?.permissions || null);
  const [selectedDealIds, setSelectedDealIds] = useState<string[]>(
    member?.assignedDeals.map((d) => d.id) || []
  );

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessLevels, setAccessLevels] = useState<AccessLevelPreset[]>([]);
  const [deals, setDeals] = useState<DealWithStats[]>([]);
  const [isLoadingPresets, setIsLoadingPresets] = useState(true);

  // Fetch presets and deals on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingPresets(true);
        const [levels, dealList] = await Promise.all([
          adminService.getAccessLevels(),
          adminService.listDeals(),
        ]);
        setAccessLevels(levels);
        setDeals(dealList);

        // Set initial permissions from preset if not editing
        if (!member) {
          const defaultPreset = levels.find((l) => l.value === 'VIEW_ONLY');
          if (defaultPreset) {
            setPermissions(defaultPreset.permissions);
          }
        }
      } catch (err) {
        console.error('Failed to load presets:', err);
      } finally {
        setIsLoadingPresets(false);
      }
    };
    fetchData();
  }, [member]);

  // When access level changes, update permissions from preset
  const handleLevelChange = (level: TeamAccessLevel) => {
    setSelectedLevel(level);
    const preset = accessLevels.find((l) => l.value === level);
    if (preset) {
      setPermissions(preset.permissions);
    }
    // DEAL_ACCESS always forces specific mode
    if (level === 'DEAL_ACCESS') {
      setDealScopeMode('specific');
    } else if (!showAdvanced) {
      setDealScopeMode('all');
      setSelectedDealIds([]);
    }
  };

  // Toggle a specific permission
  const togglePermission = (area: string, action: string) => {
    if (!permissions) return;
    setPermissions({
      ...permissions,
      areas: {
        ...permissions.areas,
        [area]: {
          ...(permissions.areas as any)[area],
          [action]: !(permissions.areas as any)[area][action],
        },
      },
    });
  };

  // Handle save
  const handleSave = async () => {
    if (!permissions) return;

    setError(null);
    setIsSaving(true);

    try {
      // Validate "specific deals" scope requires at least one deal selected
      if (dealScopeMode === 'specific' && selectedDealIds.length === 0) {
        setError('You must select at least one deal when using "Specific deals only" scope. Select a deal or switch to "All deals".');
        setIsSaving(false);
        return;
      }

      const cleanPermissions = {
        accessLevel: selectedLevel,
        dealScopeMode,
        areas: permissions.areas,
      };

      const assignedDeals = dealScopeMode === 'specific' ? selectedDealIds : [];

      if (isEditing) {
        await adminService.updateTeamMember(member.id, {
          accessLevel: selectedLevel,
          permissions: cleanPermissions,
          assignedDealIds: assignedDeals,
        });
      } else {
        if (!email.trim() || !firstName.trim() || !lastName.trim()) {
          setError('Please fill in all required fields');
          setIsSaving(false);
          return;
        }
        await adminService.createTeamMember({
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          accessLevel: selectedLevel,
          permissions: cleanPermissions,
          assignedDealIds: assignedDeals,
        });
      }

      onClose(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to save';
      setError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  // Deal scope mode - track explicitly so radio works even with 0 deals selected
  const [dealScopeMode, setDealScopeMode] = useState<'all' | 'specific'>(
    (member?.assignedDeals?.length ?? 0) > 0 || selectedLevel === 'DEAL_ACCESS' ? 'specific' : 'all'
  );

  return (
    <Transition appear show as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => onClose(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform rounded-2xl bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {isEditing ? 'Edit Team Viewer' : 'Add Team Viewer'}
                  </Dialog.Title>
                  <button
                    onClick={() => onClose(false)}
                    className="p-1 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {/* Contact Info (only for create) */}
                  {!isEditing && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Contact Information</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">First Name *</label>
                          <input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                            placeholder="John"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Last Name *</label>
                          <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                            placeholder="Smith"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block text-sm text-gray-600 mb-1">Email Address *</label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>
                  )}

                  {/* Access Level Selection */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Access Level</h3>
                    {isLoadingPresets ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {accessLevels.map((level) => {
                          const Icon = levelIcons[level.value];
                          const isSelected = selectedLevel === level.value;
                          return (
                            <button
                              key={level.value}
                              onClick={() => handleLevelChange(level.value)}
                              className={`w-full flex items-start gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className={`mt-0.5 flex-shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${
                                isSelected ? 'bg-primary-100' : 'bg-gray-100'
                              }`}>
                                <Icon className={`h-4 w-4 ${isSelected ? 'text-primary-600' : 'text-gray-500'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className={`text-sm font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                                    {level.label}
                                  </p>
                                  <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                                    isSelected ? 'border-primary-500' : 'border-gray-300'
                                  }`}>
                                    {isSelected && (
                                      <div className="h-2 w-2 rounded-full bg-primary-500" />
                                    )}
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">{level.description}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Deal Scope (show for DEAL_ACCESS always, optional for others) */}
                  {(selectedLevel === 'DEAL_ACCESS' || showAdvanced) && deals.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-3">
                        Deal Scope
                        {selectedLevel === 'DEAL_ACCESS' && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </h3>
                      <div className="space-y-2 mb-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={dealScopeMode === 'all'}
                            onChange={() => {
                              setDealScopeMode('all');
                              setSelectedDealIds([]);
                            }}
                            className="text-primary-600 focus:ring-primary-500"
                            disabled={selectedLevel === 'DEAL_ACCESS'}
                          />
                          <span className={`text-sm ${selectedLevel === 'DEAL_ACCESS' ? 'text-gray-400' : 'text-gray-700'}`}>
                            All deals
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={dealScopeMode === 'specific'}
                            onChange={() => setDealScopeMode('specific')}
                            className="text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">Specific deals only</span>
                        </label>
                      </div>

                      {/* Deal Checkboxes */}
                      {dealScopeMode === 'specific' && (
                        <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                          {deals.map((deal) => (
                            <label
                              key={deal.id}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded px-2 py-1"
                            >
                              <input
                                type="checkbox"
                                checked={selectedDealIds.includes(deal.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedDealIds([...selectedDealIds, deal.id]);
                                  } else {
                                    setSelectedDealIds(selectedDealIds.filter((id) => id !== deal.id));
                                  }
                                }}
                                className="rounded text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700">{deal.name}</span>
                              <span className="text-xs text-gray-400 ml-auto">{deal.status}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {selectedLevel !== 'DEAL_ACCESS' && dealScopeMode === 'specific' && (
                        <p className="text-xs text-gray-500 mt-2">
                          This viewer will only see data related to the selected deals.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Advanced Toggle */}
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
                  >
                    {showAdvanced ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                    Advanced: Customize permissions
                  </button>

                  {/* Advanced Permission Grid */}
                  {showAdvanced && permissions && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden mb-4 overflow-x-auto">
                      <table className="w-full text-sm min-w-[400px]">
                        <thead>
                          <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                            <th className="text-left px-4 py-2 font-medium">Area</th>
                            <th className="text-center px-2 py-2 font-medium">View</th>
                            <th className="text-center px-2 py-2 font-medium">Create</th>
                            <th className="text-center px-2 py-2 font-medium">Edit</th>
                            <th className="text-center px-2 py-2 font-medium">Delete</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {/* Dashboard */}
                          <PermRow label="Dashboard" area="dashboard" permissions={permissions} onToggle={togglePermission} actions={['view']} />
                          {/* Investors */}
                          <PermRow label="Investors" area="investors" permissions={permissions} onToggle={togglePermission} actions={['view', 'create', 'edit', 'delete']} />
                          {/* Deals */}
                          <PermRow label="Deals" area="deals" permissions={permissions} onToggle={togglePermission} actions={['view', 'create', 'edit', 'delete']} />
                          {/* Distributions */}
                          <PermRow label="Distributions" area="distributions" permissions={permissions} onToggle={togglePermission} actions={['view', 'create', 'edit', 'delete']} />
                          {/* Documents */}
                          <PermRow label="Documents" area="documents" permissions={permissions} onToggle={togglePermission} actions={['view', 'upload', 'edit', 'delete']} />
                          {/* Communications */}
                          <PermRow label="Communications" area="communications" permissions={permissions} onToggle={togglePermission} actions={['view', 'create', null, 'delete']} />
                          {/* Fundraising */}
                          <PermRow label="Fundraising/CRM" area="fundraising" permissions={permissions} onToggle={togglePermission} actions={['view', 'create', 'edit', 'delete']} />
                          {/* Accounting */}
                          <PermRow label="Accounting/Tax" area="accounting" permissions={permissions} onToggle={togglePermission} actions={['view', null, 'manage', null]} />
                          {/* Audit Log */}
                          <PermRow label="Audit Log" area="auditLog" permissions={permissions} onToggle={togglePermission} actions={['view']} />
                        </tbody>
                      </table>

                      {/* Sensitive Data Toggle */}
                      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Sensitive Data</h4>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={permissions.areas.investors.sensitive}
                            onChange={() => togglePermission('investors', 'sensitive')}
                            className="rounded text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">View SSN/FEIN & Banking Info</span>
                          <span className="text-xs text-gray-400">(requires TOTP)</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Error */}
                  {error && (
                    <div className="rounded-lg bg-red-50 p-3 mb-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                  <button
                    onClick={() => onClose(false)}
                    className="rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || (!isEditing && (!email.trim() || !firstName.trim() || !lastName.trim()))}
                    className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSaving ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Saving...
                      </span>
                    ) : isEditing ? (
                      'Save Changes'
                    ) : (
                      'Send Invitation'
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

// =============================================================================
// Permission Row Component
// =============================================================================

interface PermRowProps {
  label: string;
  area: string;
  permissions: TeamPermissions;
  onToggle: (area: string, action: string) => void;
  actions: (string | null)[]; // null = empty cell
}

function PermRow({ label, area, permissions, onToggle, actions }: PermRowProps) {
  const areaPerms = (permissions.areas as any)[area];
  if (!areaPerms) return null;

  // Pad actions to 4 columns
  const paddedActions = [...actions];
  while (paddedActions.length < 4) {
    paddedActions.push(null);
  }

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-2 text-gray-700 font-medium">{label}</td>
      {paddedActions.map((action, i) => (
        <td key={i} className="text-center px-2 py-2">
          {action !== null && areaPerms[action] !== undefined ? (
            <button
              onClick={() => onToggle(area, action)}
              className={`h-5 w-5 rounded-full border-2 inline-flex items-center justify-center transition-colors ${
                areaPerms[action]
                  ? 'border-primary-500 bg-primary-500'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <svg className={`h-3 w-3 ${areaPerms[action] ? 'text-white' : 'text-transparent'}`} viewBox="0 0 12 12" fill="none">
                <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          ) : (
            <span className="text-gray-300">—</span>
          )}
        </td>
      ))}
    </tr>
  );
}
